import React, { useState, useContext } from 'react';
import { Card, Row, Col, Button, Alert } from 'react-bootstrap';
import { SellDataRepository } from '../../data/SellDataRepository';
import { RequestParam } from '../../domain/models/RequestParam';
import { SkuLoader } from './SkuLoader';
import { useDispatch, useSelector } from 'react-redux';
import { SaleActions } from '../../presentation/redux/actions/SaleActions';
import { BalanceActions } from '../../presentation/redux/actions/BalanceActions';
import { SkuDetail, SkuDetailSection } from '../../domain/models/SkuDetail';
import { RootState } from '../../presentation/redux/store/RootState';
import { SkuProcessEngine } from './utils/SkuProcessEngine';
import { RemoteConstants } from '../../data/remote/RemoteConstants';
import authContext from '../../context/auth/authContext';

interface SkuActivationInfoProps {
    meta?: {
        id?: string;
        fecha?: string;
        lote?: string;
        clave_dist?: string;
        pdv?: string;
        referencia?: string;
        carrier_nombre?: string;
        carrier?: string;
        subprod?: number;
        destino?: string;
        monto?: number;
        expira?: string;
        estatus?: string;
        logo_path?: string;
        oferta?: string;
    };
    // Parámetros necesarios para la venta
    sku?: string;
    messageType?: string;
    requestFrom?: string;
    amount?: number;
    tipo?: string;
    geoloc?: string;
    token?: string;
    onSaleComplete?: (result: any) => void;
    onCancel?: () => void;
}

export const SkuActivationInfo: React.FC<SkuActivationInfoProps> = ({
    meta,
    sku,
    messageType = "1",
    requestFrom = "WEB",
    amount,
    tipo = "WEB",
    geoloc,
    token,
    onSaleComplete,
    onCancel
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const dispatch = useDispatch();
    const state = useSelector((s: RootState) => s.saleState);
    const AuthContext = useContext(authContext);
    const { token: authToken } = AuthContext;

    const effectiveToken = token || authToken;

    const sellRepository = new SellDataRepository();

    if (!meta) {
        return null;
    }

    const formatPhoneNumber = (phone: string) => {
        if (!phone) return '';
        return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
    };

    // Función para obtener los mensajes del SKU
    const getSkuMessages = () => {
        if (state.skuDetail && state.sku?.sku === 'ACCESOD_ACTIVACION') {
            return (
                <div className="mb-3">
                    {SkuProcessEngine.getAllMessagesComponents(state.skuDetail?.elements ?? [])}
                </div>
            );
        }
        return null;
    };

    const buildRequestParams = () => {
        const params = [
            new RequestParam("sku", sku || meta.carrier || "", "SKU del producto"),
            new RequestParam("messageType", messageType, "Tipo de mensaje"),
            new RequestParam("requestFrom", requestFrom, "Origen de la petición"),
            new RequestParam("destination", meta.destino || "", "Número telefónico"),
            new RequestParam("amount", (amount || meta.monto || 0).toString(), "Monto"),
            new RequestParam("tipo", tipo, "Tipo"),
        ];

        if (geoloc) {
            params.push(new RequestParam("geoloc", geoloc, "Geolocalización"));
        }

        if (effectiveToken) {
            params.push(new RequestParam("token", effectiveToken, "Token de sesión"));
        }

        return params;
    };

    const transformResponseToSkuDetail = (response: any): SkuDetail => {

        if (!response) {
            throw new Error('Respuesta vacía del servidor');
        }

        let dataArray;
        if (Array.isArray(response)) {
            dataArray = response;
        } else if (response.data && Array.isArray(response.data)) {
            dataArray = response.data;
        } else {
            throw new Error('La respuesta no contiene datos válidos');
        }

        const sections = dataArray.map((item: any, index: number) => {
            return new SkuDetailSection({
                ordinal: item.ordinal || index,
                web_component: item.web_component,
                modal_section: item.modal_section,
                send_to_server: item.send_to_server || false,
                web_value: item.web_value,
                web_label: item.web_label,
                param: item.param,
                constraints: null
            });
        });

        return new SkuDetail({
            response: sections
        });
    };

    const handlePurchase = async () => {

        if (!meta.referencia) {
            setError('No se encontró la referencia de la transacción');
            return;
        }

        const requestParams = buildRequestParams();

        setIsProcessing(true);
        setError(null);

        try {
            const result = await sellRepository.processSale(requestParams, meta.referencia, dispatch);

            const skuDetail = transformResponseToSkuDetail(result);

            dispatch(SaleActions.showResult(skuDetail));

            setSuccess(true);

            const tokenParam = requestParams.find(param => param.name === 'token');
            if (tokenParam && result?.code === 'OK') {
                dispatch(BalanceActions.updateBalance(tokenParam.value));
            } else if (effectiveToken && result?.code === 'OK') {
                dispatch(BalanceActions.updateBalance(effectiveToken));
            }

            if (onSaleComplete) {
                onSaleComplete(result);
            }
        } catch (err: any) {
            setError(err.message || 'Error al procesar la venta');
        } finally {
            setIsProcessing(false);
        }
    }; const handleCancel = () => {
        dispatch(SaleActions.reset());
        dispatch(SaleActions.close());

        if (onCancel) {
            onCancel();
        }
    };


    const getCompleteLogoUrl = (logoPath: string) => {
        if (!logoPath) return '';

        if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
            return logoPath;
        }

        return `${RemoteConstants.BASE_URL}${logoPath}`;
    };

    return (
        <>
            {/* Logo Section - Movido hasta arriba */}
            {meta.logo_path && (
                <div className="text-center">
                    <img
                        className='skuimg-title'
                        src={getCompleteLogoUrl(meta.logo_path)}
                        alt={meta.carrier_nombre || 'Carrier'}
                    />
                </div>
            )}

            {getSkuMessages()}

            <Card className="shadow-sm border-0" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                <Card.Body className="">
                    {/* Phone Number Section */}
                    {meta.destino && (
                        <div className="text-center mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
                            <label className="text-muted small font-weight-bold mb-1">NÚMERO TELEFÓNICO</label>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#495057', letterSpacing: '2px' }}>
                                {formatPhoneNumber(meta.destino)}
                            </div>
                        </div>
                    )}

                    {/* Amount Section */}
                    {meta.monto && (
                        <div className="text-center mb-4">
                            <div style={{
                                fontSize: '36px',
                                fontWeight: 'bold',
                                color: '#28a745',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
                            }}>
                                ${meta.monto}
                            </div>
                            <small className="text-muted">MONTO A RECARGAR</small>
                        </div>
                    )}

                    {/* Offer Section */}
                    {meta.oferta && (
                        <div className="text-center mb-4 p-3" style={{
                            backgroundColor: '#e3f2fd',
                            borderRadius: '10px',
                            border: '1px solid #bbdefb'
                        }}>
                            <i className="fas fa-receipt text-primary mr-2"></i>
                            <span className="font-weight-bold text-primary" style={{ fontSize: '18px' }}>
                                {meta.oferta}
                            </span>
                        </div>
                    )}

                    {/* Additional Details */}
                    <Row className="mt-4">
                        {meta.referencia && (
                            <Col md={12} className="mb-3">
                                <div className="d-flex justify-content-between align-items-center p-2"
                                    style={{ borderRadius: '8px' }}>
                                    <span className="text-muted font-weight-bold">REFERENCIA:</span>
                                    <span className="font-weight-bold text-dark" style={{ fontFamily: 'monospace' }}>
                                        {meta.referencia}
                                    </span>
                                </div>
                            </Col>
                        )}

                        {meta.fecha && (
                            <Col md={12} className="mb-3">
                                <div className="d-flex justify-content-between align-items-center p-2"
                                    style={{ borderRadius: '8px' }}>
                                    <span className="text-muted font-weight-bold">FECHA:</span>
                                    <span className="font-weight-bold text-dark" style={{ fontFamily: 'monospace' }}>
                                        {meta.fecha}
                                    </span>
                                </div>
                            </Col>
                        )}
                    </Row>

                    {/* Error Message */}
                    {error && (
                        <Alert variant="danger" className="mt-3 mb-3">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            {error}
                        </Alert>
                    )}

                    {/* Loading */}
                    {isProcessing && (
                        <div className="mt-3 mb-3">
                            <SkuLoader message="Procesando compra..." />
                        </div>
                    )}

                    {/* Action Buttons */}
                    {!success && (
                        <div className="mt-4">
                            <Button
                                variant="link"
                                className="float-left ml-4 mt-4"
                                onClick={handleCancel}
                                disabled={isProcessing}
                            >
                                Cancelar
                            </Button>

                            <Button
                                variant="primary"
                                className="float-right mr-4 mt-4"
                                onClick={handlePurchase}
                                disabled={isProcessing || !meta.referencia}
                            >
                                COMPRAR
                            </Button>
                        </div>
                    )}

                </Card.Body>
            </Card>
        </>
    );
};

export default SkuActivationInfo;
